import React from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal } from "./Motion";
import { LP_UI, LP_PRODUCTS } from "../lib/liveProducts";

const WARDROBE_IMG =
  "https://images.unsplash.com/photo-1603400521630-9f2de124b33b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const STORE_LABELS = {
  de: { app: ["Laden im", "App Store"], play: ["Jetzt bei", "Google Play"] },
  en: { app: ["Download on the", "App Store"], play: ["GET IT ON", "Google Play"] },
  fr: { app: ["Télécharger dans", "l'App Store"], play: ["Disponible sur", "Google Play"] },
};

const AppleBadge = ({ lang }) => {
  const l = STORE_LABELS[lang] || STORE_LABELS.de;
  return (
    <span className="flex items-center gap-3 rounded-lg border border-white/20 bg-black px-4 py-2.5 transition-colors group-hover/badge:border-champagne">
      <svg viewBox="0 0 384 512" className="h-7 w-7 fill-white" aria-hidden="true">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C71.5 141.1 16 184.6 16 273.5c0 26.3 4.8 53.5 14.4 81.5 12.8 36.9 59 127.4 107.3 125.9 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.7-.7 90.5-83 102.7-120 -65.3-30.8-61.9-90.2-61.9-92.2zM255.5 92.2c30.4-36 27.6-68.8 26.7-80.6-26.8 1.6-57.8 18.3-75.5 38.8-19.5 22-31 49.2-28.5 79.6 29 2.2 55.5-12.7 77.3-37.8z" />
      </svg>
      <span className="flex flex-col leading-none text-white">
        <span className="text-[9px] uppercase tracking-wide text-white/70">{l.app[0]}</span>
        <span className="mt-0.5 text-[15px] font-medium">{l.app[1]}</span>
      </span>
    </span>
  );
};

const PlayBadge = ({ lang }) => {
  const l = STORE_LABELS[lang] || STORE_LABELS.de;
  return (
    <span className="flex items-center gap-3 rounded-lg border border-white/20 bg-black px-4 py-2.5 transition-colors group-hover/badge:border-champagne">
      <svg viewBox="0 0 512 512" className="h-6 w-6" aria-hidden="true">
        <path fill="#00D3FF" d="M47 39c-4 4-6 11-6 20v394c0 9 2 16 6 20l1 1 221-221v-4L48 38l-1 1z" />
        <path fill="#00F076" d="M342 335l-74-74v-4l74-74 2 1 87 49c25 14 25 37 0 51l-87 50-2 1z" />
        <path fill="#FF3A44" d="M344 334l-76-76L47 479c8 9 22 10 37 1l260-146z" />
        <path fill="#FFC800" d="M344 182L84 36C69 27 55 28 47 37l221 221 76-76z" />
      </svg>
      <span className="flex flex-col leading-none text-white">
        <span className="text-[9px] uppercase tracking-wide text-white/70">{l.play[0]}</span>
        <span className="mt-0.5 text-[15px] font-medium">{l.play[1]}</span>
      </span>
    </span>
  );
};

const PopstyleShowcase = () => {
  const { lang } = useLang();
  const ui = LP_UI[lang] || LP_UI.de;
  const p = LP_PRODUCTS.find((x) => x.slug === "popstyle");
  if (!p) return null;

  return (
    <section
      className="border-t border-white/10 bg-obsidian py-24 md:py-36"
      data-testid="fashion-popstyle-showcase"
    >
      <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 md:grid-cols-2 md:gap-16 md:px-12">
        {/* copy */}
        <div>
          <Reveal>
            <span className="text-xs uppercase tracking-[0.35em] text-champagne">{ui.overline}</span>
            <h2 className="mt-5 font-serif text-5xl font-light leading-none text-white md:text-7xl">{p.name}</h2>
            <p className="mt-6 font-serif text-xl font-light italic leading-snug text-champagne/85 md:text-2xl">
              {p.tagline[lang] || p.tagline.de}
            </p>
            <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-white/65 md:text-lg">
              {p.desc[lang] || p.desc.de}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <ul className="mt-8 space-y-4">
              {(p.features[lang] || p.features.de).map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px] font-light leading-relaxed text-white/75">
                  <Check size={17} className="mt-0.5 shrink-0 text-champagne" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid="popstyle-appstore" className="group/badge">
                <AppleBadge lang={lang} />
              </a>
              <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid="popstyle-googleplay" className="group/badge">
                <PlayBadge lang={lang} />
              </a>
            </div>
            <a
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="popstyle-visit"
              className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-champagne transition-colors hover:text-champagne-light"
            >
              popstyle.ai <ArrowUpRight size={14} />
            </a>
          </Reveal>
        </div>

        {/* image */}
        <Reveal delay={0.1}>
          <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
            <img src={WARDROBE_IMG} alt="POPSTYLE" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent" />
            <span className="absolute bottom-5 left-5 rounded-full border border-champagne/50 bg-obsidian/70 px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] text-champagne backdrop-blur-sm">
              {p.category[lang] || p.category.de} · {ui.live}
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default PopstyleShowcase;
